import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { 
  ArrowRight, 
  Package2, 
  Shield, 
  Search,
  TrendingUp,
  SlidersHorizontal,
  Loader2,
  Sparkles,
  Heart,
  Star,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { truncateText, formatPrice, getDomain } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { useStore } from '@/store';
import { Product } from '@/types';
import { HeroSkeleton, CategorySkeleton, ProductGridSkeleton } from '@/components/ui/skeleton';
import { NoImagePlaceholder } from '@/components/ui/no-image-placeholder';
import { DiscountBadge } from '@/components/ui/discount-badge';

interface Category {
  category: string;
  product_id: string;
  images: string[];
}

export function ModernHomePage() {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [mobileSort, setMobileSort] = useState('newest');
  const [mobileFilter, setMobileFilter] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const { storeInfo } = useStoreInfo();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const [heroSlideError, setHeroSlideError] = useState<Record<number, boolean>>({});
  const productsRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  
  // Store hooks
  const { wishlist } = useStore();
  
  const PRODUCTS_PER_PAGE = 16;
  const heroSlides = storeInfo?.heroSlider || [];

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const domain = getDomain();
        
        // Fetch categories
        const categoriesResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/store/categories?domain=${domain}`);
        const categoriesData = await categoriesResponse.json();
        
        if (categoriesData.status) {
          const uniqueCategories = categoriesData.products
            .filter((cat: Category) => cat.category && cat.category !== 'demo' && cat.category.trim() !== '')
            .reduce((acc: Category[], current: Category) => {
              const exists = acc.find(item => item.category === current.category);
              if (!exists) {
                acc.push(current);
              }
              return acc;
            }, []);
          setCategories(uniqueCategories);
        }

        // Fetch products
        const productsResponse = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products?domain=${domain}`);
        const productsData = await productsResponse.json();

        if (productsData.status && productsData.products) {
          const activeProducts = productsData.products.filter((p: Product) => 
            p.status === 1 && p.totalStock > 0
          );
          setAllProducts(activeProducts);
          setFilteredProducts(activeProducts);
          setDisplayedProducts(activeProducts.slice(0, PRODUCTS_PER_PAGE));
          setHasMore(activeProducts.length > PRODUCTS_PER_PAGE);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle sort on mobile
  useEffect(() => {
    let sorted = [...allProducts];
    
    if (mobileSort === 'price-low') {
      sorted.sort((a, b) => parseFloat(a.sellingprice) - parseFloat(b.sellingprice));
    } else if (mobileSort === 'price-high') {
      sorted.sort((a, b) => parseFloat(b.sellingprice) - parseFloat(a.sellingprice));
    } else if (mobileSort === 'newest') {
      sorted.reverse();
    }
    
    setAllProducts(sorted);
    setPage(1);
    setDisplayedProducts(sorted.slice(0, PRODUCTS_PER_PAGE));
    setHasMore(sorted.length > PRODUCTS_PER_PAGE);
  }, [mobileSort]);

  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setTimeout(() => {
      const nextPage = page + 1;
      const startIndex = page * PRODUCTS_PER_PAGE;
      const endIndex = startIndex + PRODUCTS_PER_PAGE;
      const newProducts = allProducts.slice(startIndex, endIndex);
      
      if (newProducts.length > 0) {
        setDisplayedProducts(prev => [...prev, ...newProducts]);
        setPage(nextPage);
        setHasMore(endIndex < allProducts.length);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }, 500);
  }, [page, allProducts, loadingMore, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loadMoreProducts, hasMore, loadingMore]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleToggleWishlist = (productId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { toggleWishlist: toggleWishlistAction } = useStore.getState();
    toggleWishlistAction(productId);
    const { wishlist: currentWishlist } = useStore.getState();
    const isNowInWishlist = currentWishlist.includes(productId);
    toast.success(isNowInWishlist ? 'Added to wishlist!' : 'Removed from wishlist!', { duration: 2000 });
  };

  return (
    <>
      <Helmet>
        <title>{storeInfo?.site_name || 'Welcome'} - ...your beauty is our concern</title>
        <meta name="description" content={storeInfo?.slogan || '...your beauty is our concern'} />
      </Helmet>

      <div className="pb-32 md:pb-8 bg-white">
        {/* === HERO SECTION - VIBRANT & ENGAGING === */}
        {loading ? (
          <HeroSkeleton />
        ) : (
          <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-pink-50 md:h-[520px] py-8 sm:py-12">
            <div className="absolute inset-0 opacity-30">
              {/* Decorative shapes */}
              <motion.div 
                className="absolute top-0 left-0 w-64 h-64 bg-brand-200 rounded-full filter blur-3xl"
                animate={{ scale: [1, 1.1, 1], x: [-20, 20, -20] }}
                transition={{ duration: 15, repeat: Infinity, repeatType: "mirror" }}
              />
              <motion.div 
                className="absolute bottom-0 right-0 w-72 h-72 bg-accent-primary/20 rounded-full filter blur-3xl"
                animate={{ scale: [1, 1.2, 1], y: [20, -20, 20] }}
                transition={{ duration: 20, repeat: Infinity, repeatType: "mirror", delay: 5 }}
              />
            </div>
            <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <motion.div 
                  className="text-center md:text-left"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <span className="inline-flex items-center gap-2 bg-brand-100 text-brand-600 px-4 py-1.5 rounded-full text-sm font-semibold border border-brand-200">
                    <Sparkles className="h-4 w-4" />
                    {storeInfo?.site_name || 'Bnass Beauty'}
                  </span>
                  <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-display font-extrabold leading-tight tracking-tight text-gray-900">
                    {storeInfo?.slogan || '...your beauty is our concern'}
                  </h1>
                  <p className="mt-6 text-lg text-gray-600 max-w-lg mx-auto md:mx-0">
                    Discover our curated collection of premium beauty products designed to make you shine.
                  </p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-8">
                    <Link
                      to="/products"
                      className="inline-flex items-center gap-2 bg-brand-500 text-white px-8 py-3 rounded-full font-bold text-base hover:bg-brand-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    >
                      Shop Now
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                    <Link
                      to="/categories"
                      className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-3 rounded-full font-bold text-base hover:bg-gray-100 transition-all shadow-md"
                    >
                      Explore Categories
                    </Link>
                  </div>
                </motion.div>
                <motion.div 
                  className="block mt-6 md:mt-0"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                >
                  {heroSlides.length > 0 && heroSlides[0] ? (
                    <div className="relative">
                      <img
                        src={heroSlides[0]}
                        alt="Featured Product"
                        className="w-full h-48 sm:h-64 md:h-auto rounded-3xl shadow-2xl object-cover"
                      />
                      <motion.div 
                        className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent-primary/80 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      >
                        New!
                      </motion.div>
                    </div>
                  ) : (
                    <div className="w-full aspect-square bg-gradient-to-br from-brand-200 to-accent-primary/30 rounded-3xl shadow-2xl flex items-center justify-center">
                      <Sparkles className="w-24 h-24 text-white/50" />
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* === SEARCH BAR - Compact, non-sticky on mobile === */}
        <section className="bg-white border-b border-gray-100 md:sticky md:top-20 z-20">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 md:py-4">
            {/* Mobile: compact row with optional expandable search */}
            <div className="md:hidden flex items-center gap-2">
              {!mobileSearchOpen ? (
                <>
                  <button
                    type="button"
                    onClick={() => setMobileSearchOpen(true)}
                    className="flex-1 flex items-center gap-2 py-2.5 px-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm"
                  >
                    <Search className="h-4 w-4 shrink-0" />
                    <span>Search products...</span>
                  </button>
                  <Link
                    to="/products"
                    className="py-2.5 px-4 rounded-xl bg-brand-500 text-white font-semibold text-sm shrink-0"
                  >
                    Filter
                  </Link>
                </>
              ) : (
                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                      autoFocus
                    />
                  </div>
                  <button type="submit" className="py-2.5 px-4 rounded-xl bg-brand-500 text-white font-semibold text-sm shrink-0">
                    Search
                  </button>
                  <button type="button" onClick={() => setMobileSearchOpen(false)} className="p-2.5 rounded-xl border border-gray-200 text-gray-600 shrink-0">
                    Cancel
                  </button>
                </form>
              )}
            </div>
            {/* Desktop: full search */}
            <form onSubmit={handleSearch} className="hidden md:flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-400" />
                <input
                  type="text"
                  placeholder="Search for premium beauty products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all outline-none text-sm"
                />
              </div>
              <button type="submit" className="bg-brand-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-brand-600 transition-colors shrink-0">
                Search
              </button>
            </form>
          </div>
        </section>

        {/* === CATEGORIES - Slidable on mobile and desktop (Swiper) === */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100"
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-display font-bold text-gray-900">
                Shop by Category
              </h2>
              <Link
                to="/categories"
                className="text-brand-600 hover:text-brand-700 font-semibold text-sm flex items-center gap-1 transition-colors"
              >
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {loading ? (
              <CategorySkeleton />
            ) : (
              <div className="categories-swiper">
                <Swiper
                  modules={[Navigation, Autoplay]}
                  spaceBetween={8}
                  slidesPerView={3.2}
                  slidesPerGroup={3}
                  navigation={{ prevEl: '.cat-prev', nextEl: '.cat-next' }}
                  breakpoints={{
                    375: { slidesPerView: 3.5, spaceBetween: 8 },
                    480: { slidesPerView: 4, spaceBetween: 10 },
                    640: { slidesPerView: 5, spaceBetween: 10 },
                    1024: { slidesPerView: 6, spaceBetween: 12 },
                    1280: { slidesPerView: 7, spaceBetween: 12 },
                  }}
                  className="overflow-hidden pb-1"
                >
                  {categories.map((category) => (
                    <SwiperSlide key={category.category}>
                      <Link
                        to={`/products?category=${encodeURIComponent(category.category)}`}
                        className="group flex flex-col items-center text-center"
                      >
                        <div className="relative w-20 h-20 md:w-24 md:h-24 overflow-hidden rounded-full mb-2 shadow-sm hover:shadow-md transition-all bg-gray-100">
                          <img
                            src={category.images[0]}
                            alt={category.category}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs font-semibold text-gray-900 line-clamp-2 group-hover:text-brand-600 transition-colors max-w-[6rem]">
                          {category.category}
                        </p>
                      </Link>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    type="button"
                    className="cat-prev p-2 rounded-full bg-brand-100 hover:bg-brand-200 text-brand-600 transition-colors"
                    aria-label="Previous categories"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="cat-next p-2 rounded-full bg-brand-100 hover:bg-brand-200 text-brand-600 transition-colors"
                    aria-label="Next categories"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.section>

        {/* === MOBILE SORT - Compact single row === */}
        <section className="md:hidden bg-white border-b border-gray-100 px-3 py-2">
          <div className="flex items-center gap-2">
            <label htmlFor="sort-mobile" className="text-gray-500 text-sm font-medium shrink-0">Sort:</label>
            <select
              id="sort-mobile"
              value={mobileSort}
              onChange={(e) => setMobileSort(e.target.value)}
              className="flex-1 min-w-0 py-2 px-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-900 focus:border-brand-500 outline-none"
            >
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
            <Link
              to="/products"
              className="py-2 px-3 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors flex items-center gap-1.5 shrink-0"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filter
            </Link>
          </div>
        </section>

        {/* === PRODUCTS SECTION === */}
        <section ref={productsRef} className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-10"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-2">
                    Trending Now
                  </h2>
                  <p className="text-gray-600 text-lg">
                    Discover our most loved outfits, bags, shoes, accessories and more.
                  </p>
                </div>
                <Link
                  to="/products"
                  className="hidden md:flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-6 py-3 rounded-full font-bold transition-colors mt-2"
                >
                  View All
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </motion.div>

            {/* Products Grid - COMPLETELY NEW LAYOUT */}
            {loading ? (
              <ProductGridSkeleton count={16} />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
                {displayedProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30, scale: 0.9 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true, margin: '100px' }}
                    transition={{ delay: Math.min(index * 0.02, 0.2), duration: 0.4 }}
                    className="group h-full"
                  >
                    <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 h-full flex flex-col hover:shadow-2xl transition-all duration-300">
                      {/* Product Image - Link wraps only image */}
                      <div className="relative aspect-square overflow-hidden bg-gray-200 flex-shrink-0">
                        <Link to={`/product/${product.id}`} className="block w-full h-full">
                          {product.images && product.images.length > 0 ? (
                            <img
                              src={product.images[0]}
                              alt={product.produt_name}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <NoImagePlaceholder className="w-full h-full rounded-none" />
                          )}
                        </Link>

                        {/* Badges */}
                        <div className="absolute top-3 left-3 right-3 flex justify-between items-start gap-2 pointer-events-none">
                          <div className="flex flex-col gap-2">
                            {product.postType === "1" && (
                              <div className="bg-purple-500 text-white text-xs px-2.5 py-1 rounded-full font-bold">
                                Pre-Order
                              </div>
                            )}
                            {(() => {
                              const discountInfo = calculateProductDiscount(product, 1);
                              return discountInfo.hasDiscount && (
                                <DiscountBadge
                                  discountType={discountInfo.discountType}
                                  discountAmount={discountInfo.discountAmount}
                                />
                              );
                            })()}
                          </div>
                        </div>

                        {/* Wishlist - outside Link so click adds to wishlist without navigating */}
                        <button
                          type="button"
                          onClick={(e) => handleToggleWishlist(product.id, e)}
                          className={`absolute top-3 right-3 z-10 p-2 rounded-full shadow-lg transition-colors pointer-events-auto ${
                            wishlist.includes(product.id)
                              ? 'bg-red-500 text-white'
                              : 'bg-white/90 hover:bg-white text-gray-600 hover:text-brand-600'
                          }`}
                          aria-label={wishlist.includes(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                        >
                          <Heart className={`h-5 w-5 ${wishlist.includes(product.id) ? 'fill-current' : ''}`} />
                        </button>

                        {index === 0 && (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                            className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center opacity-20 pointer-events-none"
                          />
                        )}
                      </div>

                      {/* Product Info - title is Link; Add button is separate */}
                      <div className="p-3 md:p-4 flex flex-col flex-grow">
                          <span className="text-2xs md:text-xs text-brand-600 font-semibold uppercase tracking-wide mb-1.5 line-clamp-1">
                          {product.category || 'Fashion'}
                        </span>

                        <Link to={`/product/${product.id}`}>
                          <h3 className="text-sm md:text-base font-bold text-gray-900 group-hover:text-brand-600 transition-colors line-clamp-2 mb-2 flex-grow">
                            {truncateText(product.produt_name, 45)}
                          </h3>
                        </Link>

                        

                        <div className="flex items-center justify-between gap-2 mt-auto">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className="text-lg md:text-xl font-bold text-brand-600">
                              {formatPrice(product.sellingprice)}
                            </span>
                            {product.originalPrice && (
                              <span className="text-xs text-gray-500 line-through shrink-0">
                                {formatPrice(product.originalPrice)}
                              </span>
                            )}
                          </div>
                          <Link
                            to={`/product/${product.id}`}
                            className="bg-gradient-to-r from-brand-600 to-brand-500 text-white px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold hover:shadow-lg transition-all shrink-0 inline-flex items-center justify-center"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Loading Indicator */}
            {loadingMore && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-center items-center py-12"
              >
                <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
                <span className="ml-3 text-gray-600 font-medium">Loading more products...</span>
              </motion.div>
            )}

            {/* Load More Trigger */}
            {hasMore && !loading && (
              <div ref={loadMoreRef} className="h-20 flex items-center justify-center">
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-center"
                >
                  <p className="text-gray-500 font-medium">Scroll for more products</p>
                  <div className="flex justify-center gap-1 mt-2">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 0.6, delay: i * 0.1, repeat: Infinity }}
                        className="w-2 h-2 bg-brand-400 rounded-full"
                      />
                    ))}
                  </div>
                </motion.div>
              </div>
            )}

            {/* End of Products Message */}
            {!hasMore && !loading && displayedProducts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <Sparkles className="h-12 w-12 text-brand-400 mx-auto mb-4 opacity-50" />
                <p className="text-gray-500 text-lg font-medium mb-6">
                  You've explored all our amazing products
                </p>
                <Link
                  to="/categories"
                  className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-8 py-3 rounded-full font-bold transition-all"
                >
                  Explore Categories
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </motion.div>
            )}
          </div>
        </section>

        {/* === FEATURES SECTION - Redesigned === */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-pink-50 border-t-2 border-brand-100"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
                Why Choose Bnass Beauty
              </h2>
              <div className="h-1.5 w-28 bg-gradient-to-r from-brand-500 to-accent-primary mx-auto rounded-full" />
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  icon: Package2,
                  title: 'Curated for You',
                  desc: 'Handpicked beauty essentials that match your unique style and needs.'
                },
                {
                  icon: TrendingUp,
                  title: 'Always On Trend',
                  desc: 'Fresh drops of the latest beauty and lifestyle pieces to keep you shining.'
                },
                {
                  icon: Shield,
                  title: 'Secure & Safe',
                  desc: 'Shop with confidence. We ensure secure payments and top-quality products.'
                }
              ].map((feature, idx) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.5 }}
                  className="group text-center p-8 rounded-3xl border-2 border-transparent hover:border-brand-200 hover:shadow-2xl transition-all duration-300 bg-white hover:bg-gradient-to-br hover:from-white hover:to-brand-50"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    className="inline-flex items-center justify-center p-5 bg-gradient-to-br from-brand-100 to-brand-50 rounded-2xl mb-6 group-hover:shadow-xl transition-all"
                  >
                    <feature.icon className="h-10 w-10 text-brand-500" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

      </div>
    </>
  );
}



